/**
 * Local PRF vault: wrap/unwrap a data-encryption key (DEK) and encrypt JSON blobs.
 * Never persist raw PRF output or the unwrapped DEK — only keep DEK in memory.
 */

import { bufferToBase64Url, base64UrlToBuffer } from '@/lib/webauthn';

const GCM_IV_BYTES = 12;
const WRAPPING_INFO = new TextEncoder().encode('balance.dek.wrap.v1');

let memoryDek: CryptoKey | null = null;

function toArrayBuffer(view: Uint8Array): ArrayBuffer {
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
}

async function prfToAesKey(prfOutput: ArrayBuffer): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: WRAPPING_INFO,
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function generateDek(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

export function setMemoryDek(dek: CryptoKey | null): void {
  memoryDek = dek;
}

export function getMemoryDek(): CryptoKey | null {
  return memoryDek;
}

export function wipeMemoryDek(): void {
  memoryDek = null;
}

/**
 * Wrap DEK with a key derived from WebAuthn PRF output.
 * Returns base64url(iv || ciphertext).
 */
export async function wrapDek(
  dek: CryptoKey,
  prfOutput: ArrayBuffer
): Promise<string> {
  const wrappingKey = await prfToAesKey(prfOutput);
  const rawDek = await crypto.subtle.exportKey('raw', dek);
  const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    wrappingKey,
    rawDek
  );
  const packed = new Uint8Array(iv.length + ciphertext.byteLength);
  packed.set(iv, 0);
  packed.set(new Uint8Array(ciphertext), iv.length);
  return bufferToBase64Url(packed);
}

export async function unwrapDek(
  wrappedDek: string,
  prfOutput: ArrayBuffer
): Promise<CryptoKey> {
  const wrappingKey = await prfToAesKey(prfOutput);
  const packed = new Uint8Array(base64UrlToBuffer(wrappedDek));
  const iv = packed.slice(0, GCM_IV_BYTES);
  const ciphertext = packed.slice(GCM_IV_BYTES);
  const rawDek = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    wrappingKey,
    toArrayBuffer(ciphertext)
  );
  return crypto.subtle.importKey('raw', rawDek, { name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ]);
}

export async function encryptJson<T>(dek: CryptoKey, value: T): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES));
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    dek,
    plaintext
  );
  const packed = new Uint8Array(iv.length + ciphertext.byteLength);
  packed.set(iv, 0);
  packed.set(new Uint8Array(ciphertext), iv.length);
  return bufferToBase64Url(packed);
}

export async function decryptJson<T>(dek: CryptoKey, packedB64: string): Promise<T> {
  const packed = new Uint8Array(base64UrlToBuffer(packedB64));
  const iv = packed.slice(0, GCM_IV_BYTES);
  const ciphertext = packed.slice(GCM_IV_BYTES);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    dek,
    toArrayBuffer(ciphertext)
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}

export async function storeVaultBlob(key: string, dek: CryptoKey, value: unknown): Promise<void> {
  const { metadataDb } = await import('@/lib/db');
  const encrypted = await encryptJson(dek, value);
  await metadataDb.set(`vault:${key}`, encrypted);
}

export async function readVaultBlob<T>(key: string, dek: CryptoKey): Promise<T | null> {
  const { metadataDb } = await import('@/lib/db');
  const encrypted = await metadataDb.get(`vault:${key}`);
  if (!encrypted) return null;
  return decryptJson<T>(dek, encrypted);
}

export async function clearVaultBlobs(): Promise<void> {
  const { db } = await import('@/lib/db');
  const keys = await db.metadata
    .filter((row) => row.key.startsWith('vault:'))
    .primaryKeys();
  await db.metadata.bulkDelete(keys);
}
